<template>
  <div :class="['iq-thinking-banner', `iq-thinking-banner--${state}`]">
    <!-- ── THINKING / DONE state ── -->
    <template v-if="state === 'thinking' || state === 'done'">
      <div class="iqtb-header">
        <div class="iqtb-header__left">
          <!-- Explicit strings: Vue 2 drops any non-boolean attribute bound to `false`,
               which would remove the interactive chevron from the accessibility tree. -->
          <div
            :class="[
              'iqtb-chevron-icon',
              { 'iqtb-chevron-icon--clickable': collapsible, 'iqtb-chevron-icon--collapsed': isCollapsed }
            ]"
            :role="collapsible ? 'button' : undefined"
            :aria-label="collapsible ? (isCollapsed ? 'Expand reasoning' : 'Collapse reasoning') : undefined"
            :tabindex="collapsible ? 0 : undefined"
            @click="toggleCollapse"
            @keydown.enter="toggleCollapse"
            @keydown.space.prevent="toggleCollapse"
            :aria-hidden="collapsible ? 'false' : 'true'"
          />
          <span class="iqtb-thinking-label">{{ headerLabel }}</span>
          <span v-if="showElapsedTime && state !== 'done'" class="iqtb-elapsed">{{ formattedElapsedTime }}</span>
        </div>
      </div>

      <div v-if="!isCollapsed" class="iqtb-reasoning">
        <template v-for="(line, index) in displayLines">
          <p
            v-if="line.kind === 'text'"
            :key="`reasoning-${index}-${line.text}`"
            class="iqtb-reasoning__line"
            :class="{
              [`iqtb-reasoning__line--${index + 1}`]: true,
              'iqtb-reasoning__line--heading': line.isHeading,
            }"
          >
            {{ line.text }}
          </p>
          <div
            v-else
            :key="`skeleton-${index}`"
            class="iqtb-skeleton__line"
            :class="skeletonWidthClass(index)"
            aria-hidden="true"
          />
        </template>
      </div>

      <p v-if="resolvedCaption && !isCollapsed" class="iqtb-caption">{{ resolvedCaption }}</p>
    </template>

    <!-- ── FAILED state ── -->
    <template v-else>
      <div class="iqtb-failed-icon" aria-hidden="true">
        <Icon name="alert_circle" />
      </div>
      <div class="iqtb-failed-body">
        <p class="iqtb-failed-title">{{ failedTitle }}</p>
        <p class="iqtb-failed-message">{{ failedMessage }}</p>
      </div>
      <StandardButton
        v-if="!hideRetry"
        type="red"
        label="Retry"
        class="iqtb-retry-btn"
        @click="emit('retry')"
      />
    </template>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import { formatIQThinkingBannerLines } from "./formatIQThinkingBannerLines";
import { useIQChatStore } from "@components/IQOverlay/store/iqChatStore";
import { LD_FLAG_IQ_THINKING_BANNER_ELAPSED_TIME } from "@components/constants/launchDarklyFlagKeys";

const app = getCurrentInstance();
const { proxy } = app || {};

const VARIANT_DEFAULTS = {
  quiz: {
    title: "Thinking. This can take a couple of minutes ...",
  },
  inspection: {
    title: "Thinking. This can take a couple of minutes ...",
  },
  microlearning: {
    title: "Thinking. This can take a couple of minutes ...",
  },
  "context-validation": {
    title: "IQ is validating the video against context...",
    caption: "Analyzing your video...",
  },
};

const SKELETON_WIDTH_CLASSES = [
  "iqtb-skeleton__line--full",
  "iqtb-skeleton__line--medium",
  "iqtb-skeleton__line--short",
  "iqtb-skeleton__line--very-short",
];

const props = defineProps({
  state: {
    type: String,
    default: "thinking",
    validator: (v) => ["thinking", "failed", "done"].includes(v),
  },
  variant: {
    type: String,
    default: "quiz",
    validator: (v) => ["quiz", "inspection", "microlearning", "context-validation"].includes(v),
  },
  /** Streamed IQ reasoning from `event: thought` / `event: thinking` SSE chunks. */
  reasoning: {
    type: String,
    default: "",
  },
  /** Overrides variant default title. */
  title: {
    type: String,
    default: "",
  },
  /** Optional caption below reasoning lines (parent-provided only). */
  caption: {
    type: String,
    default: "",
  },
  /** Failed-state title (defaults to the microlearning failure copy). */
  failedTitle: {
    type: String,
    default: "Content generation failed",
  },
  /** Failed-state supporting message (defaults to the microlearning failure copy). */
  failedMessage: {
    type: String,
    default:
      "We couldn't generate the microlearning from your video. Retry, or Exit Edit",
  },
  /** Hide the built-in Retry button when the parent owns the retry action. */
  hideRetry: {
    type: Boolean,
    default: false,
  },
  /**
   * When false, ignore the IQ overlay store and render only the `reasoning` prop.
   * Used by IQSidePanel so overlay chat state cannot leak into the side pane.
   */
  useStoreReasoning: {
    type: Boolean,
    default: true,
  },
  /** Hide the live elapsed-time counter (Figma Waiting for Response). */
  hideElapsedTime: {
    type: Boolean,
    default: false,
  },
  /** Seed timestamp (ms) so the elapsed counter continues from an in-flight run, not 0. */
  startedAt: {
    type: Number,
    default: null,
  },
  /** Precomputed thinking duration (ms) for the "Thought for {N}s" done label when the
   *  banner mounts already-done (run finished while the editor was unmounted). */
  doneElapsedMs: {
    type: Number,
    default: null,
  },
  /** Current phase label shown in the header beside the timer (ER-12217). */
  phase: {
    type: String,
    default: "",
  },
  /** Shows "Still working, this is taking a moment..." when true (ER-12217). */
  stalled: {
    type: Boolean,
    default: false,
  },
  /** Enables chevron click to collapse/expand reasoning body (ER-12217). */
  collapsible: {
    type: Boolean,
    default: false,
  },
});

// ── Collapse state (ER-12217) ──
// A banner that mounts already-done (result arrived while the editor was
// unmounted) starts collapsed, same as one that transitions into done.
const isCollapsed = ref(props.state === "done");

const toggleCollapse = () => {
  if (!props.collapsible) return;
  isCollapsed.value = !isCollapsed.value;
};

// Auto-collapse when state becomes "done"
watch(
  () => props.state,
  (newState) => {
    if (newState === "done") {
      isCollapsed.value = true;
    }
  }
);

const resolvedTitle = computed(() => props.title || VARIANT_DEFAULTS[props.variant].title);

const resolvedCaption = computed(() => {
  if (props.caption) return props.caption;
  return VARIANT_DEFAULTS[props.variant].caption || "";
});

// ── Header label (ER-12217) ──
const headerLabel = computed(() => {
  if (props.state === "done") {
    const ms =
      typeof props.doneElapsedMs === "number" ? props.doneElapsedMs : elapsedTime.value;
    const seconds = Math.floor(ms / 1000);
    return `Thought for ${seconds}s`;
  }
  if (props.stalled) {
    return "Still working, this is taking a moment…";
  }
  if (props.phase) {
    return props.phase;
  }
  return resolvedTitle.value;
});

const chatStore = useIQChatStore();
const { currentReasoning } = storeToRefs(chatStore);

/** Prop override for Storybook/tests; otherwise same stream as MiniIQOverlay / IQOverlay. */
const effectiveReasoning = computed(() => {
  if (!props.useStoreReasoning) {
    return props.reasoning || "";
  }
  return props.reasoning || currentReasoning.value || "";
});
const streamedLines = computed(() => formatIQThinkingBannerLines(effectiveReasoning.value));

/** Streaming is assumed unless the store explicitly reports thinking has stopped. */
const isStreamingThinking = computed(() => {
  if (!props.useStoreReasoning) {
    return true;
  }
  return chatStore.isThinking !== false;
});

const showElapsedTime = computed(() => elapsedTimeEnabled.value && !props.hideElapsedTime);

/**
 * Lines captured while the stream was still running. Once thinking stops the banner keeps
 * showing them — the reasoning is cleared at the end of a run, and a blank stream would
 * otherwise flip every slot back to a skeleton while the banner is still on screen.
 */
const streamedLinesOnStop = ref(null);

watch(
  [streamedLines, isStreamingThinking],
  ([lines, isStreaming]) => {
    if (!isStreaming) return;
    const textLines = lines.filter((line) => line.kind === "text");
    streamedLinesOnStop.value = textLines.length ? textLines : null;
  },
  { immediate: true }
);

const displayLines = computed(() => {
  if (isStreamingThinking.value) return streamedLines.value;
  return streamedLinesOnStop.value ?? streamedLines.value;
});

function skeletonWidthClass(index) {
  return SKELETON_WIDTH_CLASSES[index] || SKELETON_WIDTH_CLASSES[SKELETON_WIDTH_CLASSES.length - 1];
}

const emit = defineEmits(["retry"]);

// ── Live elapsed-time counter (ER-11265) ──
// Gated by an LD kill switch: on by default, set the flag to false to hide the counter.
const elapsedTimeEnabled = ref(false);
const startTime = ref(null);
const elapsedTime = ref(0);
let elapsedInterval = null;

const formattedElapsedTime = computed(() => {
  if (startTime.value === null) return "";
  const seconds = Math.floor(elapsedTime.value / 1000);
  return `${seconds}s`;
});

const startElapsedTimer = () => {
  if (props.hideElapsedTime || !elapsedTimeEnabled.value || elapsedInterval) return;
  const seed = typeof props.startedAt === "number" ? props.startedAt : Date.now();
  startTime.value = seed;
  elapsedTime.value = Date.now() - seed;
  elapsedInterval = setInterval(() => {
    elapsedTime.value = Date.now() - startTime.value;
  }, 1000);
};

const stopElapsedTimer = () => {
  if (elapsedInterval) {
    clearInterval(elapsedInterval);
    elapsedInterval = null;
  }
};

watch(
  () => props.state,
  (newState, oldState) => {
    if (newState === "thinking" && oldState !== "thinking") {
      startElapsedTimer();
    } else if (newState !== "thinking") {
      stopElapsedTimer();
    }
  }
);

onMounted(async () => {
  try {
    elapsedTimeEnabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_IQ_THINKING_BANNER_ELAPSED_TIME, true)) === true;
  } catch {
    elapsedTimeEnabled.value = true;
  }
  if (props.state === "thinking") {
    startElapsedTimer();
  }
});

onUnmounted(() => {
  stopElapsedTimer();
});
</script>

<style scoped>
/* ── Base ── */
.iq-thinking-banner {
  width: 100%;
  border-radius: 10px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ── Thinking variant ── */
.iq-thinking-banner--thinking {
  background-color: #f2edff;
}

/* ── Done variant ── */
.iq-thinking-banner--done {
  background-color: #f2edff;
  padding: 12px 20px;
}

/* ── Failed variant ── */
.iq-thinking-banner--failed {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 16px 12px;
  border-radius: 12px;
}

/* ── Chevron icon ── */
.iqtb-chevron-icon {
  width: 12px;
  height: 12px;
  background-color: #9388cc;
  border-radius: 2px;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.iqtb-chevron-icon--clickable {
  cursor: pointer;
}

.iqtb-chevron-icon--clickable:hover {
  background-color: #7b70b3;
}

.iqtb-chevron-icon--collapsed {
  transform: rotate(-90deg);
}

/* ── Thinking header ── */
.iqtb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 22px;
}

.iqtb-header__left {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.iqtb-thinking-label {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: normal;
  color: #475467;
  white-space: nowrap;
}

/* Live elapsed-time counter (ER-11265) */
.iqtb-elapsed {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: normal;
  color: #475467;
}

/* ── Reasoning lines ── */
.iqtb-reasoning {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.iqtb-reasoning__line {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  color: #475467;
  margin: 0;
  word-break: break-word;
}

.iqtb-reasoning__line--heading {
  font-weight: 600;
  color: #475467;
}

.iqtb-reasoning__line--4 {
  color: rgba(147, 136, 204, 0.85);
}

/* ── Skeleton lines ── */
.iqtb-skeleton__line {
  height: 8px;
  border-radius: 4px;
  background-color: #c9c0ee;
  animation: iqtb-shimmer 1.4s ease-in-out infinite;
}

.iqtb-skeleton__line--full {
  width: 100%;
}
.iqtb-skeleton__line--medium {
  width: 55%;
}
.iqtb-skeleton__line--short {
  width: 49%;
}
.iqtb-skeleton__line--very-short {
  width: 43%;
}

@keyframes iqtb-shimmer {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

/* ── Thinking caption ── */
.iqtb-caption {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: normal;
  color: #475467;
  margin: 0;
}

/* ── Failed icon ── */
.iqtb-failed-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  background-color: #fee2e2;
  border-radius: 16px;
}

/* ── Failed body ── */
.iqtb-failed-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.iqtb-failed-title {
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: normal;
  color: #991b1b;
  margin: 0;
}

.iqtb-failed-message {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: normal;
  color: #991b1b;
  margin: 0;
}

/* ── Retry button ── */
.iqtb-retry-btn {
  flex-shrink: 0;
  padding: 8px 16px;
  background-color: #dc2626;
  border: none;
  border-radius: 6px;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 13px;
  line-height: normal;
  color: #ffffff;
  cursor: pointer;
  white-space: nowrap;
}

.iqtb-retry-btn:hover {
  background-color: #b91c1c;
}
</style>
